/**
 * STREAMING dispatch — which streaming surface a Drop gets.
 *
 * Three answers, and the choice is the POV's, not the data's: a roster POV
 * renders the creator team, a CREATOR renders its own player-and-tabs section,
 * anything else falls back to the Drop's own channels. Split out of
 * `DropSectionRenderer` — it is the longest single case there and the file was
 * past the 400-line ceiling that file's own header cites.
 */
import React from "react";
import {dropLayout} from "../dropLayout";
import {PlayingNowSection} from "@/widgets/PlayingNowSection";
import {StreamingSection} from "@/widgets/StreamingSection";
import {CreatorSection} from "@/widgets/CreatorSection";
import {CreatorTeamSection} from "@/widgets/CreatorTeamSection";
import type {StreamingSettings} from "@/hooks/hub/useGetHub";
import type {DropRenderContext} from "./dropRenderContext";

export function renderStreamingSection(ctx: DropRenderContext): React.ReactNode {
    const {drop, slug, liveLayout, identitySlot, onLoginRequired, referralUserId} = ctx;

    /*
     * Roster-based Drops (publisher / esports / brand / community / agency)
     * render the creator team rather than the Drop's own channels.
     *
     * Keyed on the POV, because the POV is what the operator chose. This
     * required `streamingTeamId` to be set, so a Drop created as Publisher
     * WITHOUT pasting a roster fell through to the legacy Community Streams
     * widget — the Drop's own two channels under a heading reading "Creator
     * Team". The roster being empty is a state the roster section should show,
     * not a reason to render a different section.
     *
     * CreatorTeamSection is slug-driven because the filtered roster comes from
     * /drops/slug/:slug/roster, which is the only endpoint that applies both
     * the roster's game filter and the Drop's campaign window.
     */
    if (dropLayout(drop ?? {}) === "PUBLISHER" && slug) {
        return <>
            <CreatorTeamSection
                slug={slug}
                /*
                 * What the player shows when nobody is live and no clips exist —
                 * the ordinary state of a publisher Drop before launch.
                 */
                fallbackPosterUrl={drop?.heroSection?.heroImageUrl ||
                    drop?.creatorHeaderImageUrl}
                fallbackVideoUrl={drop?.trailerUrl}
                onLoginRequired={onLoginRequired}
                referralUserId={referralUserId}
            />
            {/*
              * Coverage under endorsement, in that order and never merged.
              *
              * The roster above is who the publisher works with; this is
              * everyone the platform says is in the category. Rendering them as
              * one list would put strangers under the publisher's endorsement,
              * so they are two sections with their own headings — and this one
              * removes itself entirely when nobody is live.
              */}
            <PlayingNowSection slug={slug} />
        </>;
    }

    if (drop?.dropType !== "CREATOR") {
        return <StreamingSection
            streamingSettings={drop?.streamingSettings as unknown as StreamingSettings}
            hubName={drop?.name}
            /*
             * StreamingSection expects HubType (`COMPETITION` | `COMMUNITY`
             * | `BRAND` | `CREATOR` | `GENERAL`). dropType is typed as
             * string upstream; cast at the boundary.
             */
            hubType={drop?.dropType as Parameters<typeof StreamingSection>[0]["hubType"]}
            surfaceId={drop?.id}
        />;
    }

    /*
     * Default tab heuristic: SOCIAL when the creator has no streaming
     * platforms configured but does have social accounts. Otherwise STREAM.
     * Prevents landing on an empty Highlights row for social-only creators.
     */
    const streamCount = (drop.streamingSettings as { featuredStreams?: unknown[] })?.featuredStreams?.length ?? 0;
    const socialCount = drop.socials?.length ?? 0;
    const hasStreams = streamCount > 0;
    const hasSocials = socialCount > 0;
    const initialTab = !hasStreams && hasSocials ? "SOCIAL" : "STREAM";

    /*
     * CreatorSection suppresses the Challenges tab when hasChallenges is
     * falsy. Must be passed explicitly — the prop is optional so tsc won't
     * warn if omitted, but the tab becomes permanently hidden.
     */
    const hasChallenges = !!(drop.competitionId || (drop.questConfigs?.length ?? 0) > 0);

    return <CreatorSection
        surfaceId={drop.id}
        dropSlug={slug}
        streamingSettings={drop.streamingSettings as unknown as StreamingSettings}
        hubName={drop.name}
        initialTab={initialTab}
        hasChallenges={hasChallenges}
        liveLayout={liveLayout}
        identitySlot={identitySlot}
    />;
}

/*
 * MISSIONS dispatch — prefer the slot-system strip when the challenge has
 * active quest configs, otherwise fall back to the legacy MissionsSection
 * (which has its own loading/empty states + FAQ + content blocks). Picking
 * one render path at a time avoids the previous bug where both rendered
 * simultaneously, duplicating mission content. Once the strip handles
 * every shape MissionsSection handles, the fallback can retire.
 */
